<!--
  Responsive data table:
  - Desktop: standard <table>
  - Mobile (<md): stacked card-per-row layout using column `label` fields
-->
<template>
  <div class="w-full">
    <!-- Desktop table -->
    <div class="hidden md:block overflow-x-auto rounded-lg border border-border">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-muted/50 border-b border-border">
            <th
              v-for="col in columns"
              :key="col.key"
              :class="cn('px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap', col.headerClass)"
            >
              {{ col.label }}
            </th>
            <th v-if="$slots.actions" class="px-4 py-3 text-right font-medium text-muted-foreground">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-border bg-white">
          <tr v-if="loading">
            <td :colspan="columns.length + ($slots.actions ? 1 : 0)" class="px-4 py-8 text-center">
              <div class="flex flex-col items-center gap-3">
                <div v-for="i in 5" :key="i" class="w-full flex gap-4">
                  <AppSkeleton v-for="col in columns" :key="col.key" class="h-4 flex-1" />
                </div>
              </div>
            </td>
          </tr>
          <tr v-else-if="!rows.length">
            <td :colspan="columns.length + ($slots.actions ? 1 : 0)" class="px-4 py-12 text-center text-muted-foreground">
              <slot name="empty">
                <div class="flex flex-col items-center gap-2">
                  <component :is="emptyIcon" v-if="emptyIcon" class="h-10 w-10 opacity-30" />
                  <p class="text-sm font-medium">{{ emptyText }}</p>
                </div>
              </slot>
            </td>
          </tr>
          <tr
            v-else
            v-for="(row, idx) in rows"
            :key="rowKey ? row[rowKey] : idx"
            :class="cn('hover:bg-muted/30 transition-colors', rowClass?.(row))"
            @click="$emit('row-click', row)"
          >
            <td
              v-for="col in columns"
              :key="col.key"
              :class="cn('px-4 py-3 text-foreground', col.cellClass)"
            >
              <slot :name="`cell-${col.key}`" :row="row" :value="row[col.key]">
                {{ row[col.key] ?? '—' }}
              </slot>
            </td>
            <td v-if="$slots.actions" class="px-4 py-3 text-right">
              <slot name="actions" :row="row" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile card list -->
    <div class="md:hidden space-y-2">
      <div v-if="loading" class="space-y-3">
        <AppSkeleton v-for="i in 4" :key="i" class="h-24 w-full rounded-lg" />
      </div>
      <div
        v-else-if="!rows.length"
        class="rounded-lg border border-border bg-white px-4 py-10 text-center text-muted-foreground text-sm"
      >
        <slot name="empty">{{ emptyText }}</slot>
      </div>
      <div
        v-else
        v-for="(row, idx) in rows"
        :key="rowKey ? row[rowKey] : idx"
        :class="cn('rounded-lg border border-border bg-white px-4 py-3 shadow-sm', rowClass?.(row))"
        @click="$emit('row-click', row)"
      >
        <div class="grid grid-cols-2 gap-x-4 gap-y-2">
          <template v-for="col in columns.filter(c => !c.mobileHide)" :key="col.key">
            <div>
              <p class="text-xs text-muted-foreground">{{ col.label }}</p>
              <div class="mt-0.5 text-sm font-medium text-foreground">
                <slot :name="`cell-${col.key}`" :row="row" :value="row[col.key]">
                  {{ row[col.key] ?? '—' }}
                </slot>
              </div>
            </div>
          </template>
        </div>
        <div v-if="$slots.actions" class="mt-3 flex justify-end gap-2 border-t border-border pt-3">
          <slot name="actions" :row="row" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { cn } from '@/lib/utils'
import AppSkeleton from './AppSkeleton.vue'

defineProps({
  columns:   { type: Array,    required: true },
  rows:      { type: Array,    default: () => [] },
  loading:   { type: Boolean,  default: false },
  rowKey:    { type: String,   default: 'id' },
  emptyText: { type: String,   default: 'No records found' },
  emptyIcon: { type: [Object, Function], default: null },
  rowClass:  { type: Function, default: null },
})
defineEmits(['row-click'])
</script>
